/* eslint-disable jsx-a11y/anchor-is-valid */
import { ButtonBase, Link, makeStyles } from '@material-ui/core';
import {
  forEach, get, groupBy, isEmpty, isNull, map, max, min, sortBy, split,
} from 'lodash';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import config from '@/configs/config';
import Slider from '../components/Slider';
import { FILTER } from '../constants';
import menuContext from '../contexts/menu';
import { FILTER_IMAGE } from './Filters';

const useTitleStyle = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  iconContainer: {
    height: 41,
  },
  icon: {
    height: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(1),
  },
  description: {
    fontSize: 12,
    fontWeight: 'normal',
  },
}));

function Title() {
  const classes = useTitleStyle();

  return (
    <div className={classes.root}>
      <div className={classes.iconContainer}>
        <img className={classes.icon} src={FILTER_IMAGE[FILTER.GOODS_STATUS]} alt="" />
      </div>
      <div className={classes.content}>
        <span>機台內現貨優惠</span>
        <span className={classes.description}>刷卡開門，拿了就走，優惠折扣將自動套用。</span>
      </div>
    </div>
  );
}

const useItemStyle = makeStyles((theme) => ({
  root: {
    alignItems: 'stretch',
    background: 'white',
    borderRadius: 8,
    boxShadow: 'rgba(157,157,157,.5) 1px 1px 5px 0px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    padding: theme.spacing(1),
    position: 'relative',
    textAlign: 'left',
    width: '100%',
  },
  coverImageContainer: {
    position: 'relative',
    paddingBottom: '50%',
  },
  coverImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
  },
  timeTag: {
    background: (props) => props.bgColor || theme.palette.campaign.main,
    borderRadius: '0 8px 8px 0',
    color: 'white',
    fontSize: 12,
    left: 0,
    padding: theme.spacing(0.5, 1.5),
    position: 'absolute',
    top: 16,
    zIndex: 1,
  },
  name: {
    background: (props) => props.bgColor || theme.palette.campaign.main,
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
    padding: theme.spacing(0, 1),
  },
  content: {
    alignItems: 'flex-end',
    display: 'flex',
    fontSize: 12,
    justifyContent: 'space-between',
  },
  productName: {
    fontWeight: 'bold',
  },
  link: {
    alignSelf: 'flex-end',
    color: 'black',
    marginTop: theme.spacing(1),
  },
  comingSoonMask: {
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    color: 'white',
    display: 'flex',
    fontSize: 14,
    fontWeight: 'bold',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 2,
  },
}));

function Item({
  bgColor,
  coverImageUrl,
  endTime,
  labelImageUrl,
  name,
  onClick,
  products,
  providerName,
  startTime,
}) {
  const isOngoing = moment().isBetween(startTime, endTime);
  const classes = useItemStyle({ bgColor: isOngoing ? bgColor : '#25006a' });
  const startTimeStr = moment(startTime).utcOffset(8).format('HH:mm');
  const endTimeStr = moment(endTime).utcOffset(8).format('HH:mm');

  return (
    <ButtonBase
      className={classes.root}
      onClick={() => onClick({
        template: 'providerCampaign',
        data: {
          campaignTitle: name,
          providerName,
          products,
        },
      })}
    >
      {isOngoing ? null : (
        <div className={classes.comingSoonMask}>優惠活動即將開始</div>
      )}
      {startTime && endTime ? (
        <span className={classes.timeTag}>
          {`${startTimeStr} ~ ${endTimeStr}`}
        </span>
      ) : null}
      <div className={classes.coverImageContainer}>
        <img
          alt=""
          className={classes.coverImage}
          src={`${config.RESOURCE_URI}${coverImageUrl}`}
          onError={(e) => {
            e.currentTarget.src = `${config.RESOURCE_URI}provider_default.png`;
          }}
        />
        { labelImageUrl ? (
          <img
            alt=""
            className={classes.coverImage}
            src={`${config.RESOURCE_URI}${labelImageUrl}`}
          />
        ) : null}
      </div>
      <span className={classes.name}>{name}</span>
      <div className={classes.content}>
        <span className={classes.productName}>
          {providerName && `【${providerName}】`}
        </span>
        <Link
          className={classes.link}
          underline="always"
        >
          查看優惠
        </Link>
      </div>
    </ButtonBase>
  );
}

export default function OfflineCampaigns({ onClick }) {
  const { menuCache } = useContext(menuContext);
  const { fridgeId: selectedFridgeId } = useParams() || {};
  const {
    currentCampaign,
    currentProviderCampaign,
    productProviderInfo,
    products,
  } = menuCache || {};
  const fridgeCampaigns = get(currentCampaign, [selectedFridgeId], {});
  const providerCampaigns = get(currentProviderCampaign, [selectedFridgeId], {});

  const data = useMemo(() => {
    const fooSpaceResult = [];
    const providerResult = [];
    const todayStartTime = +moment().utcOffset(8).startOf('day');
    const todayEndTime = +moment().utcOffset(8).endOf('day');

    forEach(
      groupBy(
        map(fridgeCampaigns, (item, id) => ({ ...item, id })),
        (item) => {
          const {
            campaignEndTime,
            campaignStartTime,
            campaignTitle,
            campaignTitleBgColor,
            coverImage,
          } = item || {};

          return `${campaignTitle}_-${coverImage}_-${campaignTitleBgColor}_-${campaignStartTime}_-${campaignEndTime}`;
        }
      ),
      (campaignProducts, id) => {
        const [name, labelImageUrl, bgColor, startTime, endTime] = split(id, '_-');
        const resultProducts = [];
        let coverImageUrl = '';

        forEach(campaignProducts, (item) => {
          const { id: productId, sellPrice } = item || {};
          const product = get(products, [productId], null);

          if (isNull(product)) return;

          const { name: productName, price, provider: providerName } = product || {};

          const providerId = btoa(encodeURIComponent(providerName || ''));
          const provider = get(productProviderInfo, [providerId], null);

          if (isNull(provider)) return;

          const { coverImage } = provider;

          if (isEmpty(coverImageUrl)) coverImageUrl = coverImage;

          resultProducts.push({
            name: productName,
            price,
            sellPrice,
          });
        });

        if (isEmpty(resultProducts)) return;

        fooSpaceResult.push({
          bgColor,
          coverImageUrl,
          endTime: min([todayEndTime, parseInt(endTime, 10)]),
          id,
          labelImageUrl,
          name,
          products: resultProducts,
          startTime: max([todayStartTime, parseInt(startTime, 10)]),
        });
      }
    );

    forEach(providerCampaigns, (campaign) => {
      const {
        applicableChannel,
        campaignTitle,
        start,
        end,
        providerName,
        products: campaignProducts,
        template,
      } = campaign;
      // eslint-disable-next-line no-bitwise
      if (applicableChannel === 2) return;
      // 排除倒數即食優惠活動
      if (template === 'D0002') return;
      if (isEmpty(campaignProducts)) return;

      const providerId = btoa(encodeURIComponent(providerName || ''));
      const provider = get(productProviderInfo, [providerId], null);

      if (isNull(provider)) return;

      const { coverImage } = provider || {};

      providerResult.push({
        id: `${providerName}_-${campaignTitle}_-${coverImage}_-${start}_-${end}`,
        coverImageUrl: coverImage,
        endTime: min([todayEndTime, parseInt(end, 10)]),
        name: campaignTitle,
        products: campaignProducts,
        providerName,
        startTime: max([todayStartTime, parseInt(start, 10)]),
      });
    });

    return [
      ...sortBy(fooSpaceResult, 'startTime'),
      ...sortBy(providerResult, 'startTime'),
    ];
  }, [fridgeCampaigns, providerCampaigns, productProviderInfo, products]);

  return isEmpty(data) ? null : (
    <Slider
      data={data}
      renderItem={(itemProps) => <Item {...itemProps} onClick={onClick} />}
      title={<Title />}
    />
  );
}
